<template>
  <div class="complete-information-page">
    <div class="complete-information-shell">
      <header class="complete-information-header">
        <div class="brand-lockup">
          <img v-if="logoUrl" :src="logoUrl" :alt="companyName" class="brand-logo" />
          <span v-if="companyName" class="brand-name">{{ companyName }}</span>
        </div>
        <router-link class="back-link p-button p-button-sm p-button-outlined" to="/References/courier">
          <i class="pi pi-arrow-right" aria-hidden="true"></i>
          <span>لیست رفرنس‌ها</span>
        </router-link>
      </header>

      <main class="complete-information border-round-3xl">
        <div class="complete-information-card surface-card">
          <div class="complete-information-heading">
            <span class="complete-information-eyebrow">تکمیل اطلاعات مرسوله</span>
            <h1>اطلاعات سفارش</h1>
            <p>لطفاً اطلاعات گیرنده، فرستنده، آدرس‌ها و بسته را بررسی و تکمیل نمایید.</p>
          </div>

          <div class="complete-information-body grid formgrid">
            <Stepper class="col-12 order-info-stepper" linear>
              <StepperPanel header="اطلاعات گیرنده و فرستنده">
                <template #content="{ nextCallback }">
                  <div class="flex flex-column gap-4">
                    <!-- Sender Section -->
                    <div class="section-title-wrap">
                      <span class="header-title-sender">فرستنده</span>
                    </div>
                    <div class="party-card">
                      <div class="party-form-grid grid formgrid w-full">
                        <div class="col-12 mb-3">
                          <div class="flex align-items-center gap-3">
                            <Checkbox
                              v-model="senderIsMe"
                              :binary="true"
                              inputId="sender_is_me"
                              @change="onSenderIsMeChange"
                            />
                            <label for="sender_is_me" class="font-medium">من فرستنده هستم</label>
                          </div>
                        </div>
                        <div v-if="!senderIsMe" class="col-12">
                          <div class="selector-row mb-3">
                            <label class="selector-label">انتخاب فرستنده</label>
                            <div class="selector-control">
                              <div class="autocomplete-input-wrap">
                                <AutoComplete
                                  ref="senderAutocomplete"
                                  v-model="selectedSender"
                                  :suggestions="senderSuggestions"
                                  @complete="searchSender"
                                  @item-select="onSenderSelect"
                                  field="name"
                                  placeholder="جستجوی فرستنده..."
                                  class="selector-autocomplete"
                                  :dropdown="false"
                                  :complete-on-focus="false"
                                >
                                  <template #item="{ item }">
                                    <div class="customer-option">
                                      <span>{{ item.name }}</span>
                                      <small v-if="item.mobile">{{ item.mobile }}</small>
                                    </div>
                                  </template>
                                </AutoComplete>
                                <button
                                  type="button"
                                  class="autocomplete-dropdown-trigger"
                                  aria-label="باز کردن لیست فرستنده"
                                  @mousedown.prevent
                                  @click="openSenderList"
                                >
                                  <i class="pi pi-chevron-down" aria-hidden="true"></i>
                                </button>
                              </div>
                              <Button
                                icon="pi pi-plus"
                                label="ایجاد فرستنده"
                                severity="success"
                                icon-class="ml-2"
                                @click="openCreateCustomerModal('sender')"
                              />
                            </div>
                          </div>
                        </div>
                        <div v-if="senderDisplayData?.id" class="col-12">
                          <div class="customer-summary-card">
                            <div class="customer-summary-grid grid">
                              <div class="summary-item col-12 md:col-4">
                                <span class="summary-label">
                                  {{ senderDisplayData.legal_type === 'company' ? 'نام شرکت:' : 'نام و نام خانوادگی:' }}
                                </span>
                                <div class="summary-value">
                                  {{ formatPartyName(senderDisplayData) }}
                                </div>
                              </div>
                              <div class="summary-item col-12 md:col-4">
                                <span class="summary-label">موبایل:</span>
                                <div class="summary-value">{{ senderDisplayData.mobile ?? '-' }}</div>
                              </div>
                              <div class="summary-item col-12 md:col-4">
                                <span class="summary-label">کد ملی:</span>
                                <div class="summary-value">{{ senderDisplayData.national_code ?? '-' }}</div>
                              </div>
                              <div class="summary-item col-12 md:col-4">
                                <span class="summary-label">نوع مشتری:</span>
                                <div class="summary-value">
                                  {{ senderDisplayData.legal_type === 'company' ? 'حقوقی' : 'حقیقی' }}
                                </div>
                              </div>
                              <div class="summary-item col-12 md:col-4">
                                <span class="summary-label">ایمیل:</span>
                                <div class="summary-value">{{ senderDisplayData.email ?? '-' }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Receiver Section -->
                    <div class="section-title-wrap">
                      <span class="header-title">گیرنده</span>
                    </div>
                    <div class="party-card">
                      <div class="party-form-grid grid formgrid w-full">
                        <div class="col-12 mb-3">
                          <div class="flex align-items-center gap-3">
                            <Checkbox
                              v-model="receiverIsMe"
                              :binary="true"
                              inputId="receiver_is_me"
                              @change="onReceiverIsMeChange"
                            />
                            <label for="receiver_is_me" class="font-medium">من گیرنده هستم</label>
                          </div>
                        </div>
                        <div v-if="!receiverIsMe" class="col-12">
                          <div class="selector-row mb-3">
                            <label class="selector-label">انتخاب گیرنده</label>
                            <div class="selector-control">
                              <div class="autocomplete-input-wrap">
                                <AutoComplete
                                  ref="receiverAutocomplete"
                                  v-model="selectedReceiver"
                                  :suggestions="receiverSuggestions"
                                  @complete="searchReceiver"
                                  @item-select="onReceiverSelect"
                                  field="name"
                                  placeholder="جستجوی گیرنده..."
                                  class="selector-autocomplete"
                                  :dropdown="false"
                                  :complete-on-focus="false"
                                >
                                  <template #item="{ item }">
                                    <div class="customer-option">
                                      <span>{{ item.name }}</span>
                                      <small v-if="item.mobile">{{ item.mobile }}</small>
                                    </div>
                                  </template>
                                </AutoComplete>
                                <button
                                  type="button"
                                  class="autocomplete-dropdown-trigger"
                                  aria-label="باز کردن لیست گیرنده"
                                  @mousedown.prevent
                                  @click="openReceiverList"
                                >
                                  <i class="pi pi-chevron-down" aria-hidden="true"></i>
                                </button>
                              </div>
                              <Button
                                icon="pi pi-plus"
                                label="ایجاد گیرنده"
                                severity="success"
                                icon-class="ml-2"
                                @click="openCreateCustomerModal('receiver')"
                              />
                            </div>
                          </div>
                        </div>
                        <div v-if="receiverDisplayData?.id" class="col-12">
                          <div class="customer-summary-card">
                            <div class="customer-summary-grid grid">
                              <div class="summary-item col-12 md:col-4">
                                <span class="summary-label">
                                  {{ receiverDisplayData.legal_type === 'company' ? 'نام شرکت:' : 'نام و نام خانوادگی:' }}
                                </span>
                                <div class="summary-value">
                                  {{ formatPartyName(receiverDisplayData) }}
                                </div>
                              </div>
                              <div class="summary-item col-12 md:col-4">
                                <span class="summary-label">موبایل:</span>
                                <div class="summary-value">{{ receiverDisplayData.mobile ?? '-' }}</div>
                              </div>
                              <div class="summary-item col-12 md:col-4">
                                <span class="summary-label">کد ملی:</span>
                                <div class="summary-value">{{ receiverDisplayData.national_code ?? '-' }}</div>
                              </div>
                              <div class="summary-item col-12 md:col-4">
                                <span class="summary-label">نوع مشتری:</span>
                                <div class="summary-value">
                                  {{ receiverDisplayData.legal_type === 'company' ? 'حقوقی' : 'حقیقی' }}
                                </div>
                              </div>
                              <div class="summary-item col-12 md:col-4">
                                <span class="summary-label">ایمیل:</span>
                                <div class="summary-value">{{ receiverDisplayData.email ?? '-' }}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="step-actions justify-content-end">
                    <Button
                      label="اطلاعات پستی مبدا و مقصد"
                      :disabled="!canProceedToStep2 || isSavingSenderReceiver"
                      :loading="isSavingSenderReceiver"
                      @click="
                        nextCallback();
                        saveSenderAndReceiver();
                      "
                    />
                  </div>
                </template>
              </StepperPanel>
              <StepperPanel header="اطلاعات پستی مبدا و مقصد">
                <template #content="{ prevCallback, nextCallback }">
                  <div class="flex flex-column">
                    <div class="section-title-wrap"><span class="header-title">انتخاب آدرس فرستنده</span></div>

                    <div
                      class="address-panel flex-auto flex justify-content-center align-items-center font-medium"
                    >
                      <div class="order-addresses w-full text-right">
                        <AddEditCustomerAddress action-type="add" :customer-id="currentData.sender_id" />
                        <div v-if="senderAddresses.length" class="address-list">
                          <div
                            v-for="(address, index) in senderAddresses"
                            :key="index"
                            class="address-item grid text-right"
                          >
                            <div class="col-1">
                              <RadioButton
                                id="sender_addr"
                                name="sender_addr"
                                :value="address.id"
                                v-model="selectedSenderAddress"
                                @change="selectSenderAddress"
                              />
                            </div>
                            <div class="col-4">آدرس: {{ address.address }}</div>
                            <div class="col-2">کد پستی: {{ address.postal_code }}</div>
                            <div class="col-2">کشور: {{ address.city?.country?.fa_name }}</div>
                            <div class="col-2">شهر: {{ address.city?.en_name }}</div>
                            <div class="col-1">
                              <EssenceDelete
                                essence-source="badge"
                                essence-type="CustomerAddress"
                                :essence-id="address.id"
                                lang="fa"
                                emitter-name="refresh-complete-information"
                              />
                              <AddEditCustomerAddress
                                :customer-id="currentData.sender_id"
                                action-type="edit"
                                :prev-data="address"
                              />
                            </div>
                          </div>
                        </div>
                        <div v-else>
                          <p class="text-center address-not-found">آدرسی برای فرستنده یافت نشد</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="address-block flex flex-column">
                    <div class="section-title-wrap"><span class="header-title">انتخاب آدرس گیرنده</span></div>

                    <div
                      class="address-panel flex-auto flex justify-content-center align-items-center font-medium"
                    >
                      <div class="order-addresses w-full text-right">
                        <AddEditCustomerAddress
                          v-if="currentData.receiver_id"
                          action-type="add"
                          :customer-id="currentData.receiver_id"
                        />
                        <div v-if="receiverAddresses.length" class="address-list">
                          <div
                            v-for="(address, index) in receiverAddresses"
                            :key="index"
                            class="address-item grid text-right"
                          >
                            <div class="col-1">
                              <RadioButton
                                id="receiver_addr"
                                name="receiver_addr"
                                :value="address.id"
                                v-model="selectedReceiverAddress"
                                @change="selectReceiverAddress"
                              />
                            </div>
                            <div class="col-4">آدرس: {{ address.address }}</div>
                            <div class="col-2">کد پستی: {{ address.postal_code }}</div>
                            <div class="col-2">کشور: {{ address.city?.country?.fa_name }}</div>
                            <div class="col-2">شهر: {{ address.city?.en_name }}</div>
                            <div class="col-1">
                              <EssenceDelete
                                essence-source="badge"
                                essence-type="CustomerAddress"
                                :essence-id="address.id"
                                lang="fa"
                                emitter-name="refresh-complete-information"
                              />
                              <AddEditCustomerAddress
                                :customer-id="currentData.receiver_id"
                                action-type="edit"
                                :prev-data="address"
                              />
                            </div>
                          </div>
                        </div>
                        <div v-else>
                          <p v-if="currentData.receiver_id" class="text-center address-not-found">
                            آدرسی برای گیرنده یافت نشد
                          </p>
                          <p v-else class="text-center text-color-secondary address-not-found mt-6">
                            مشخصات گیرنده وارد نشده است
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="step-actions justify-content-between">
                    <Button label="اطلاعات گیرنده و فرستنده" severity="secondary" @click="prevCallback" />
                    <Button label="اطلاعات بار" @click="nextCallback" />
                  </div>
                </template>
              </StepperPanel>
              <StepperPanel header="اطلاعات بار">
                <template #content="{ prevCallback }">
                  <div class="flex flex-column">
                    <div class="package-panel">
                      <OrderPackageDetail v-model="currentData.order_package" :order-id="orderId" />
                    </div>
                  </div>
                  <div class="step-actions justify-content-between">
                    <Button label="اطلاعات پستی مبدا و مقصد" severity="secondary" @click="prevCallback" />
                    <Button
                      label="ثبت نهایی"
                      :loading="isSubmittingPackageInfo"
                      :disabled="isSubmittingPackageInfo"
                      @click="submitPackageInfo(currentData.order_package)"
                    />
                  </div>
                </template>
              </StepperPanel>
            </Stepper>
          </div>
        </div>
      </main>
    </div>
  </div>

  <!-- Create Customer Modal -->
  <Dialog
    v-model:visible="createCustomerModalVisible"
    modal
    class="customer-create-dialog"
    :style="{ width: 'min(720px, calc(100vw - 2rem))' }"
    :show-header="false"
    :closable="!creatingCustomer"
    :close-on-escape="!creatingCustomer"
  >
    <form class="customer-dialog" dir="rtl" @submit.prevent="createCustomer">
      <header class="customer-dialog-header">
        <div class="customer-dialog-title-row">
          <span class="customer-dialog-icon" aria-hidden="true">
            <i class="pi pi-user-plus"></i>
          </span>
          <div>
            <span class="customer-dialog-kicker">ثبت مشتری جدید</span>
            <h2>{{ createCustomerType === 'sender' ? 'ایجاد فرستنده جدید' : 'ایجاد گیرنده جدید' }}</h2>
            <p>
              {{
                createCustomerType === 'sender'
                  ? 'مشخصات فرستنده را وارد کنید تا در این سفارش قابل انتخاب باشد.'
                  : 'مشخصات گیرنده را وارد کنید تا در این سفارش قابل انتخاب باشد.'
              }}
            </p>
          </div>
        </div>
        <button
          type="button"
          class="customer-dialog-close"
          aria-label="بستن"
          :disabled="creatingCustomer"
          @click="createCustomerModalVisible = false"
        >
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="customer-dialog-body">
        <section class="customer-dialog-section customer-type-section">
          <div class="customer-section-heading">
            <div>
              <span class="customer-section-kicker">دسته‌بندی</span>
              <h3>نوع مشتری</h3>
            </div>
            <i class="pi pi-users customer-section-symbol" aria-hidden="true"></i>
          </div>
          <div class="customer-type-options">
            <label
              class="customer-type-option"
              :class="{ 'is-selected': newCustomer.legal_type === 'person' }"
              for="new_legal_person"
            >
              <RadioButton
                v-model="newCustomer.legal_type"
                inputId="new_legal_person"
                name="new_legal_type"
                value="person"
              />
              <span>
                <strong>حقیقی</strong>
                <small>شخص مستقل</small>
              </span>
            </label>
            <label
              class="customer-type-option"
              :class="{ 'is-selected': newCustomer.legal_type === 'company' }"
              for="new_legal_company"
            >
              <RadioButton
                v-model="newCustomer.legal_type"
                inputId="new_legal_company"
                name="new_legal_type"
                value="company"
              />
              <span>
                <strong>حقوقی</strong>
                <small>شرکت یا سازمان</small>
              </span>
            </label>
          </div>
        </section>

        <section class="customer-dialog-section">
          <div class="customer-section-heading">
            <div>
              <span class="customer-section-kicker">اطلاعات هویتی</span>
              <h3>{{ newCustomer.legal_type === 'person' ? 'نام و مشخصات فردی' : 'مشخصات شرکت' }}</h3>
            </div>
            <i class="pi pi-id-card customer-section-symbol" aria-hidden="true"></i>
          </div>
          <div class="customer-dialog-grid">
            <div v-if="newCustomer.legal_type === 'person'" class="customer-dialog-field">
              <label for="new_first_name">نام <span class="required-mark">*</span></label>
              <InputText
                v-model="newCustomer.first_name"
                id="new_first_name"
                required
                class="w-full"
                placeholder="نام"
              />
            </div>
            <div v-if="newCustomer.legal_type === 'person'" class="customer-dialog-field">
              <label for="new_last_name">نام خانوادگی <span class="required-mark">*</span></label>
              <InputText
                v-model="newCustomer.last_name"
                id="new_last_name"
                required
                class="w-full"
                placeholder="نام خانوادگی"
              />
            </div>
            <div v-if="newCustomer.legal_type === 'company'" class="customer-dialog-field customer-dialog-field--wide">
              <label for="new_company_name">نام شرکت <span class="required-mark">*</span></label>
              <InputText
                v-model="newCustomer.company_name"
                id="new_company_name"
                required
                class="w-full"
                placeholder="نام شرکت"
              />
            </div>
          </div>
        </section>

        <section class="customer-dialog-section">
          <div class="customer-section-heading">
            <div>
              <span class="customer-section-kicker">راه‌های ارتباطی</span>
              <h3>اطلاعات تماس</h3>
            </div>
            <i class="pi pi-phone customer-section-symbol" aria-hidden="true"></i>
          </div>
          <div class="customer-dialog-grid">
            <div class="customer-dialog-field">
              <label for="new_mobile">شماره موبایل <span class="required-mark">*</span></label>
              <InputText
                v-model="newCustomer.mobile"
                id="new_mobile"
                required
                class="w-full"
                placeholder="شماره موبایل"
              />
            </div>
            <div class="customer-dialog-field">
              <label for="new_email">ایمیل</label>
              <InputText v-model="newCustomer.email" id="new_email" class="w-full" placeholder="ایمیل" />
            </div>
            <div class="customer-dialog-field">
              <label for="new_national_code">کد ملی</label>
              <InputText
                v-model="newCustomer.national_code"
                id="new_national_code"
                class="w-full"
                placeholder="کد ملی"
              />
            </div>
            <div class="customer-dialog-field">
              <label for="new_phone">تلفن</label>
              <InputText v-model="newCustomer.phone" id="new_phone" class="w-full" placeholder="تلفن" />
            </div>
            <div class="customer-dialog-field">
              <label for="new_passport">شماره پاسپورت</label>
              <InputText
                v-model="newCustomer.passport_number"
                id="new_passport"
                class="w-full"
                placeholder="شماره پاسپورت"
              />
            </div>
          </div>
        </section>
      </div>

      <footer class="customer-dialog-footer">
        <span class="customer-dialog-tip">
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          پس از ثبت، مشتری در فهرست انتخاب قابل مشاهده خواهد بود.
        </span>
        <div class="customer-dialog-actions">
          <Button
            type="button"
            label="انصراف"
            severity="secondary"
            outlined
            :disabled="creatingCustomer"
            @click="createCustomerModalVisible = false"
          />
          <Button
            type="submit"
            :label="createCustomerType === 'sender' ? 'ثبت فرستنده' : 'ثبت گیرنده'"
            icon="pi pi-check"
            severity="success"
            :loading="creatingCustomer"
            :disabled="creatingCustomer"
          />
        </div>
      </footer>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import GlobalService from '../service/GlobalService';
import { ReferenceService } from '@/service/ReferenceService';
import { useRoute } from 'vue-router';
import emitter from '@/utils/eventBus';
import AddEditCustomerAddress from '@/components/CustomerComponent/AddEditCustomerAddress.vue';
import EssenceDelete from '@/components/Global/EssenceDelete.vue';
import OrderPackageDetail from '@/components/CustomerComponent/OrderPackageDetail.vue';
import AutoComplete from 'primevue/autocomplete';
import { useCompanyBrand } from '@/composables/useCompanyBrand';
import { formatPartyName } from '@/utils/globalHelpers';

const selectedSenderAddress = ref(null);
const selectedReceiverAddress = ref(null);
const GlobalServiceItem = ref(new GlobalService());
const ReferenceServiceItem = ref(new ReferenceService());
const currentData = ref({ sender: {}, receiver: {}, legal_type: 'person' });
const senderAddresses = ref([]);
const receiverAddresses = ref([]);
const router = useRoute();
const orderId = Number(router.params.id);
const { companyName, companyLogoUrl, loadCompanySettings } = useCompanyBrand();

const logoUrl = computed(() => companyLogoUrl.value);

// Customer selection
const allCustomers = ref([]);
const selectedSender = ref(null);
const selectedReceiver = ref(null);
const senderSuggestions = ref([]);
const receiverSuggestions = ref([]);
const senderIsMe = ref(false);
const receiverIsMe = ref(false);
const senderAutocomplete = ref(null);
const receiverAutocomplete = ref(null);

const senderDisplayData = computed(() => {
  if (senderIsMe.value) {
    return currentData.value.sender ?? {};
  }
  return selectedSender.value ?? currentData.value.sender ?? {};
});

const receiverDisplayData = computed(() => {
  if (receiverIsMe.value) {
    return currentData.value.receiver ?? {};
  }
  return selectedReceiver.value ?? currentData.value.receiver ?? {};
});

const canProceedToStep2 = computed(() => {
  return Boolean(senderDisplayData.value?.id) && Boolean(receiverDisplayData.value?.id);
});

// Create customer modal
const createCustomerModalVisible = ref(false);
const createCustomerType = ref('sender');
const creatingCustomer = ref(false);
const isSavingSenderReceiver = ref(false);
const isSubmittingPackageInfo = ref(false);
const newCustomer = ref({
  legal_type: 'person',
  first_name: '',
  last_name: '',
  company_name: '',
  mobile: '',
  email: '',
  national_code: '',
  phone: '',
  passport_number: ''
});

const normalizeCustomer = (customer = {}) => {
  return {
    ...customer,
    name: formatPartyName(customer)
  };
};

const filterCustomerSuggestions = (event) => {
  const isFocusSearch = event?.originalEvent?.type === 'focus';
  const query = isFocusSearch ? '' : String(event?.query ?? '').trim().toLowerCase();

  return allCustomers.value.filter((customer) => {
    const searchableValue = [
      customer.name,
      customer.company_name,
      customer.first_name,
      customer.last_name,
      customer.mobile
    ]
      .filter((value) => value !== null && value !== undefined)
      .join(' ')
      .toLowerCase();

    return searchableValue.includes(query);
  });
};

const searchSender = (event) => {
  senderSuggestions.value = filterCustomerSuggestions(event);
};

const searchReceiver = (event) => {
  receiverSuggestions.value = filterCustomerSuggestions(event);
};

const openSenderList = () => {
  senderSuggestions.value = allCustomers.value;
  senderAutocomplete.value?.show?.(true);
};

const openReceiverList = () => {
  receiverSuggestions.value = allCustomers.value;
  receiverAutocomplete.value?.show?.(true);
};

const onSenderSelect = async (event) => {
  const customer = event.value;
  const fullData = await fetchCustomerById(customer.id);
  selectedSender.value = normalizeCustomer(fullData);
};

const onReceiverSelect = async (event) => {
  const customer = event.value;
  const fullData = await fetchCustomerById(customer.id);
  selectedReceiver.value = normalizeCustomer(fullData);
};

const fetchCustomerById = async (id) => {
  try {
    const response = await GlobalServiceItem.value.getCustomerById(id);
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch customer:', error);
    return {};
  }
};

const onSenderIsMeChange = (checked) => {
  if (checked) {
    selectedSender.value = null;
  }
};

const onReceiverIsMeChange = (checked) => {
  if (checked) {
    selectedReceiver.value = null;
  }
};

const openCreateCustomerModal = (type) => {
  createCustomerType.value = type;
  newCustomer.value = {
    legal_type: 'person',
    first_name: '',
    last_name: '',
    company_name: '',
    mobile: '',
    email: '',
    national_code: '',
    phone: '',
    passport_number: ''
  };
  createCustomerModalVisible.value = true;
};

const createCustomer = async () => {
  creatingCustomer.value = true;
  try {
    const data = { ...newCustomer.value };
    if (data.legal_type === 'person') {
      data.company_name = null;
    }
    const response = await ReferenceServiceItem.value.createCustomer(data);
    const createdCustomerId = response?.data?.data?.customer_id;
    emitter.emit('toast', { type: 'success', message: 'مشتری با موفقیت ایجاد شد' });
    createCustomerModalVisible.value = false;
    const customers = await loadCustomerList();
    const createdCustomer = customers.find(
      (customer) =>
        String(customer.id) === String(createdCustomerId) ||
        (data.mobile && String(customer.mobile) === String(data.mobile))
    );

    if (createdCustomer) {
      const selectedCustomer = normalizeCustomer({ ...data, ...createdCustomer });
      if (createCustomerType.value === 'receiver') {
        receiverIsMe.value = false;
        selectedReceiver.value = selectedCustomer;
      } else {
        senderIsMe.value = false;
        selectedSender.value = selectedCustomer;
      }
    }
  } catch (error) {
    emitter.emit('toast-axios', error);
  } finally {
    creatingCustomer.value = false;
  }
};

const loadCustomerList = async () => {
  try {
    const response = await GlobalServiceItem.value.getCustomerList();
    allCustomers.value = Array.isArray(response.data.data) ? response.data.data.map(normalizeCustomer) : [];
    senderSuggestions.value = allCustomers.value;
    receiverSuggestions.value = allCustomers.value;
    return allCustomers.value;
  } catch (error) {
    console.error('Failed to load customers:', error);
    return allCustomers.value;
  }
};

const saveSenderAndReceiver = async () => {
  if (isSavingSenderReceiver.value) return;
  isSavingSenderReceiver.value = true;
  try {
    const senderId = senderIsMe.value ? currentData.value.sender?.id : senderDisplayData.value?.id;
    const receiverId = receiverIsMe.value ? currentData.value.sender?.id : receiverDisplayData.value?.id;

    if (senderId && !currentData.value.sender_id) {
      await GlobalServiceItem.value.setSenderInfo(orderId, { sender_id: senderId });
    }

    if (receiverId && !currentData.value.receiver_id) {
      await ReferenceServiceItem.value.setReceiverById(orderId, receiverId);
    }

    await getOrderInformation();
    emitter.emit('toast', { type: 'success', message: 'اطلاعات فرستنده و گیرنده ذخیره شد' });
  } catch (error) {
    emitter.emit('toast-axios', error);
  } finally {
    isSavingSenderReceiver.value = false;
  }
};

const selectSenderAddress = async () => {
  const data = {
    address_id: selectedSenderAddress.value,
    name: formatPartyName(senderDisplayData.value),
    mobile: senderDisplayData.value.mobile
  };
  GlobalServiceItem.value
    .setOriginAddress(orderId, data)
    .then(() => {
      emitter.emit('toast', { type: 'success', message: 'آدرس فرستنده با موفقیت انتخاب شد' });
    })
    .catch((error) => emitter.emit('toast-axios', error));
};

const selectReceiverAddress = async () => {
  const data = {
    address_id: selectedReceiverAddress.value,
    name: formatPartyName(receiverDisplayData.value),
    mobile: receiverDisplayData.value.mobile
  };
  GlobalServiceItem.value
    .setDestinationAddress(orderId, data)
    .then(() => {
      emitter.emit('toast', { type: 'success', message: 'آدرس گیرنده با موفقیت انتخاب شد' });
    })
    .catch((error) => emitter.emit('toast-axios', error));
};

const getOrderInformation = async () => {
  GlobalServiceItem.value
    .getOrderInformation(orderId)
    .then((data) => {
      currentData.value = data.data.data;
      if (currentData.value.sender) {
        currentData.value.sender = normalizeCustomer(currentData.value.sender);
        selectedSender.value = currentData.value.sender;
        selectedSenderAddress.value = currentData.value?.senderAddress?.address_id ?? null;
      }
      if (currentData.value.receiver) {
        currentData.value.receiver = normalizeCustomer(currentData.value.receiver);
        selectedReceiver.value = currentData.value.receiver;
        selectedReceiverAddress.value = currentData.value?.receiverAddress?.address_id ?? null;
      }
      senderAddresses.value = currentData.value.sender?.addresses ?? [];
      receiverAddresses.value = currentData.value.receiver?.addresses ?? [];
    })
    .catch(() => {})
    .finally(() => {});
};

const submitPackageInfo = async (data) => {
  if (isSubmittingPackageInfo.value) return;

  isSubmittingPackageInfo.value = true;
  try {
    const response = await GlobalServiceItem.value.setOrderPackageInfo(orderId, data);
    const payload = response?.data ?? {};

    if (payload.errors && Object.keys(payload.errors).length > 0) {
      const messages = Object.values(payload.errors)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
        .map((value) => String(value).trim());

      emitter.emit('toast', {
        type: 'error',
        title: 'خطا در ثبت اطلاعات بار',
        message: messages.join('، ') || payload.message || 'ثبت اطلاعات بار انجام نشد'
      });
      return;
    }

    emitter.emit('toast', { type: 'success', message: 'تکمیل اطلاعات با موفقیت انجام شد' });
  } catch (error) {
    emitter.emit('toast-axios', error);
  } finally {
    isSubmittingPackageInfo.value = false;
  }
};

emitter.on('refresh-complete-information', () => {
  getOrderInformation();
});

onMounted(() => {
  loadCompanySettings();
  getOrderInformation();
  loadCustomerList();
});
</script>

<style scoped lang="scss">
.complete-information-page {
  min-height: 100vh;
  padding: clamp(1rem, 3vw, 3rem) clamp(0.75rem, 4vw, 4rem);
  color: var(--fm-text-strong);
  direction: rtl;
  background:
    radial-gradient(circle at top right, color-mix(in srgb, var(--primary-color) 10%, transparent), transparent 28rem),
    var(--surface-ground);
}

.complete-information-shell {
  width: min(100%, 1180px);
  margin: 0 auto;
}

.complete-information-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.brand-lockup {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.75rem;
}

.brand-logo {
  width: auto;
  max-width: 10rem;
  max-height: 3rem;
  object-fit: contain;
}

.brand-name {
  overflow: hidden;
  color: var(--fm-text-muted);
  font-size: 0.9rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.back-link {
  flex-shrink: 0;
  gap: 0.45rem;
  text-decoration: none;
}

.complete-information {
  padding: 0.35rem;
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--primary-color) 80%, #fff),
    color-mix(in srgb, var(--primary-color) 15%, transparent) 55%,
    transparent 100%
  );
  box-shadow: var(--fm-shadow-md);
}

.complete-information-card {
  padding: clamp(1rem, 3vw, 2rem);
  border-radius: 1.35rem;
}

.complete-information-heading {
  max-width: 42rem;
  margin: 0 auto 1.75rem;
  text-align: center;
}

.complete-information-eyebrow {
  display: inline-flex;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  color: var(--fm-primary);
  background: var(--fm-primary-soft);
  font-size: 0.78rem;
  font-weight: 800;
}

.complete-information-heading h1 {
  margin: 0.65rem 0 0.35rem;
  font-size: clamp(1.45rem, 3vw, 2.1rem);
}

.complete-information-heading p {
  margin: 0;
  color: var(--fm-text-muted);
  line-height: 1.8;
}

.complete-information-body {
  padding: 0;
}

.order-info-stepper {
  direction: rtl;

  :deep(.p-stepper-nav) {
    margin-bottom: 1.75rem;
  }

  :deep(.p-stepper-title) {
    margin-right: 0.6rem;
    font-family: iransans, serif;
    font-weight: 700;
  }

  :deep(.p-stepper-panel) {
    min-width: 0;
  }
}

.section-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding-inline: 0.25rem;
}

.section-title-wrap::before {
  width: 0.35rem;
  height: 1.4rem;
  border-radius: 999px;
  background: var(--primary-color);
  content: '';
}

.header-title,
.header-title-sender {
  color: var(--fm-text-strong);
  font-size: 1.1rem;
  font-weight: 800;
}

.party-card,
.address-panel,
.package-panel {
  min-width: 0;
  padding: clamp(0.8rem, 2vw, 1.25rem);
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-radius-lg);
  background: var(--fm-surface);
  box-shadow: var(--fm-shadow-sm);
}

.party-form-grid {
  margin: 0;
}

.selector-row {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.75rem;
}

.selector-label {
  flex: 0 0 8rem;
  color: var(--fm-text-muted);
  font-weight: 700;
}

.selector-control {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  gap: 0.5rem;
}

.selector-autocomplete {
  display: block;
  width: 100%;
  min-width: 0;
}

.selector-autocomplete :deep(.p-inputtext) {
  width: 100%;
}

.autocomplete-input-wrap {
  position: relative;
  width: 100%;
  min-width: 0;
}

.autocomplete-input-wrap .selector-autocomplete :deep(.p-inputtext) {
  padding-inline-start: 2.25rem;
}

.autocomplete-dropdown-trigger {
  position: absolute;
  top: 50%;
  left: 0.55rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: 0;
  color: var(--fm-text-muted);
  background: transparent;
  cursor: pointer;
  transform: translateY(-50%);
}

.autocomplete-dropdown-trigger:hover {
  color: var(--fm-text-strong);
}

.autocomplete-dropdown-trigger:focus-visible {
  border-radius: 0.25rem;
  outline: 2px solid color-mix(in srgb, var(--primary-color) 45%, transparent);
  outline-offset: 2px;
}

.selector-control .p-button {
  align-self: stretch;
  white-space: nowrap;
}

.customer-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.75rem;
}

.customer-option small {
  color: var(--fm-text-muted);
  direction: ltr;
  font-size: 0.75rem;
}

.customer-summary-card {
  margin-top: 0.5rem;
  padding: 1rem;
  border: 1px solid color-mix(in srgb, var(--primary-color) 15%, var(--fm-border));
  border-radius: var(--fm-radius-md);
  background: linear-gradient(135deg, var(--fm-primary-soft), var(--fm-surface));
}

.customer-summary-grid {
  margin: -0.25rem;
}

.summary-item {
  min-width: 0;
  padding: 0.5rem 0.75rem;
}

.summary-label {
  display: block;
  margin-bottom: 0.2rem;
  color: var(--fm-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.summary-value {
  min-height: 1.5rem;
  color: var(--fm-text-strong);
  font-weight: 700;
  overflow-wrap: anywhere;
}

.step-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--fm-border);
}

.address-block {
  margin-top: 2rem;
}

.order-addresses {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.address-item {
  display: grid;
  grid-template-columns: auto minmax(12rem, 2fr) repeat(3, minmax(7rem, 1fr)) auto;
  align-items: center;
  min-width: 0;
  gap: 0.75rem;
  margin: 0;
  padding: 0.85rem;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-radius-md);
  background: var(--fm-surface-muted);
}

.address-item > [class*='col-'] {
  width: auto;
  min-width: 0;
  padding: 0;
  overflow-wrap: anywhere;
}

.address-item > .col-4 {
  min-width: 12rem;
}

.address-not-found {
  margin: 1.5rem 0;
  color: var(--fm-text-muted);
  font-size: 0.95rem;
}

.customer-create-dialog {
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--primary-color) 18%, var(--fm-border));
  border-radius: 1.25rem;
  background: var(--fm-surface);
  box-shadow: 0 1.5rem 4rem rgb(15 23 42 / 18%);

  :deep(.p-dialog-content) {
    padding: 0;
    overflow: hidden;
    background: transparent;
  }
}

.customer-dialog {
  color: var(--fm-text-strong);
}

.customer-dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.35rem 1.5rem 1.15rem;
  border-bottom: 1px solid color-mix(in srgb, var(--primary-color) 10%, var(--fm-border));
  background: linear-gradient(135deg, var(--fm-primary-soft), var(--fm-surface));
}

.customer-dialog-title-row {
  display: flex;
  align-items: flex-start;
  min-width: 0;
  gap: 0.9rem;
}

.customer-dialog-icon {
  display: grid;
  flex: 0 0 auto;
  width: 3.25rem;
  height: 3.25rem;
  place-items: center;
  border-radius: 1rem;
  color: #fff;
  background: linear-gradient(135deg, var(--primary-color), color-mix(in srgb, var(--primary-color) 68%, #0f172a));
  box-shadow: 0 0.6rem 1.25rem color-mix(in srgb, var(--primary-color) 22%, transparent);
  font-size: 1.3rem;
}

.customer-dialog-kicker,
.customer-section-kicker {
  display: block;
  color: var(--primary-color);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.customer-dialog-header h2,
.customer-section-heading h3 {
  margin: 0;
  color: var(--fm-text-strong);
}

.customer-dialog-header h2 {
  margin-top: 0.2rem;
  font-size: 1.25rem;
  line-height: 1.45;
}

.customer-dialog-header p {
  max-width: 38rem;
  margin: 0.35rem 0 0;
  color: var(--fm-text-muted);
  font-size: 0.82rem;
  line-height: 1.8;
}

.customer-dialog-close {
  display: grid;
  flex: 0 0 auto;
  width: 2.2rem;
  height: 2.2rem;
  place-items: center;
  border: 1px solid var(--fm-border);
  border-radius: 0.7rem;
  color: var(--fm-text-muted);
  background: var(--fm-surface);
  cursor: pointer;
  transition: 0.2s ease;
}

.customer-dialog-close:hover:not(:disabled) {
  border-color: color-mix(in srgb, var(--primary-color) 45%, var(--fm-border));
  color: var(--primary-color);
  background: var(--fm-primary-soft);
}

.customer-dialog-close:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.customer-dialog-close:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.customer-dialog-body {
  display: flex;
  max-height: min(58vh, 36rem);
  flex-direction: column;
  gap: 0.9rem;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
}

.customer-dialog-section {
  padding: 1rem;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-radius-md);
  background: linear-gradient(135deg, color-mix(in srgb, var(--primary-color) 3%, var(--fm-surface)), var(--fm-surface-muted));
}

.customer-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
}

.customer-section-heading h3 {
  margin-top: 0.15rem;
  font-size: 0.98rem;
}

.customer-section-symbol {
  display: grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  border-radius: 0.65rem;
  color: var(--primary-color);
  background: var(--fm-primary-soft);
  font-size: 0.9rem;
}

.customer-type-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.customer-type-option {
  display: flex;
  align-items: center;
  min-height: 4.25rem;
  gap: 0.75rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-radius-md);
  background: var(--fm-surface);
  cursor: pointer;
  transition: 0.2s ease;
}

.customer-type-option:hover {
  border-color: color-mix(in srgb, var(--primary-color) 40%, var(--fm-border));
  background: var(--fm-primary-soft);
}

.customer-type-option.is-selected {
  border-color: color-mix(in srgb, var(--primary-color) 70%, var(--fm-border));
  background: color-mix(in srgb, var(--primary-color) 8%, var(--fm-surface));
  box-shadow: 0 0.35rem 0.9rem color-mix(in srgb, var(--primary-color) 10%, transparent);
}

.customer-type-option > span {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.customer-type-option strong {
  color: var(--fm-text-strong);
  font-size: 0.88rem;
}

.customer-type-option small {
  color: var(--fm-text-muted);
  font-size: 0.73rem;
}

.customer-type-option :deep(.p-radiobutton) {
  flex: 0 0 auto;
}

.customer-dialog-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.95rem;
}

.customer-dialog-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.42rem;
}

.customer-dialog-field--wide {
  grid-column: 1 / -1;
}

.customer-dialog-field label {
  color: var(--fm-text-muted);
  font-size: 0.79rem;
  font-weight: 800;
}

.required-mark {
  color: var(--fm-danger);
}

.customer-dialog-field :deep(.p-inputtext) {
  width: 100%;
  min-height: 2.7rem;
  border-radius: 0.7rem;
}

.customer-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem 1.5rem 1.35rem;
  border-top: 1px solid color-mix(in srgb, var(--primary-color) 10%, var(--fm-border));
  background: var(--fm-surface);
}

.customer-dialog-tip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: var(--fm-text-muted);
  font-size: 0.75rem;
  line-height: 1.7;
}

.customer-dialog-tip i {
  flex: 0 0 auto;
  color: var(--primary-color);
}

.customer-dialog-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 0.65rem;
}

.customer-dialog-actions :deep(.p-button) {
  min-width: 8rem;
  justify-content: center;
}

@media (max-width: 767px) {
  .complete-information-page {
    padding: 0.75rem;
  }

  .complete-information-header {
    align-items: stretch;
    flex-direction: column;
  }

  .back-link {
    align-self: flex-start;
  }

  .complete-information-card {
    padding: 1rem 0.75rem;
  }

  .selector-row {
    align-items: stretch;
    flex-direction: column;
  }

  .selector-label {
    flex-basis: auto;
  }

  .selector-control {
    display: flex;
    width: 100%;
    flex-direction: column;
  }

  .selector-control .p-button,
  .step-actions .p-button {
    width: 100%;
  }

  .step-actions,
  .step-actions.justify-content-end,
  .step-actions.justify-content-between {
    justify-content: stretch;
  }

  .address-item {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .address-item > [class*='col-'],
  .address-item > .col-4 {
    width: 100%;
    min-width: 0;
  }

  .customer-create-dialog {
    width: calc(100vw - 1.5rem) !important;
  }

  .customer-dialog-header,
  .customer-dialog-body,
  .customer-dialog-footer {
    padding-right: 1rem;
    padding-left: 1rem;
  }

  .customer-dialog-header {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  .customer-dialog-icon {
    width: 2.85rem;
    height: 2.85rem;
    border-radius: 0.85rem;
  }

  .customer-dialog-header h2 {
    font-size: 1.08rem;
  }

  .customer-dialog-header p {
    font-size: 0.77rem;
  }

  .customer-type-options,
  .customer-dialog-grid {
    grid-template-columns: 1fr;
  }

  .customer-dialog-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .customer-dialog-actions {
    width: 100%;
  }

  .customer-dialog-actions :deep(.p-button) {
    min-width: 0;
    flex: 1 1 0;
  }
}
</style>
